import {useFluxStore} from 'client/framework';
import type {ReactNode} from 'react';
import React from 'react';
import {Dropdown, Icon, MenuListItem, Modal, SegmentedControl, Text} from '@albert-io/atomic';
import CustomerFeedbackDialogue from 'client/CustomerFeedback/CustomerFeedbackDialogue.react';
import sessionStore from 'client/Session/SessionStore';
import {Link} from 'react-router';
import type {ThemePreference} from 'client/components/Theme/ThemeContext';
import {ThemeContext} from 'client/components/Theme/ThemeContext';

import {useGlobalNavUser} from '../useGlobalNavUser';
import useNavItems from '../useNavItems';

const APPEARANCE_OPTIONS: {value: ThemePreference; label: ReactNode}[] = [
  {
    value: 'light',
    label: (
      <>
        <Icon icon='sun' iconStyle='regular' className='fa-xs mr-x1' />
        Light
      </>
    )
  },
  {
    value: 'dark',
    label: (
      <>
        <Icon icon='moon' iconStyle='regular' className='fa-xs mr-x1' />
        Dark
      </>
    )
  },
  {
    value: 'system',
    label: (
      <>
        <Icon icon='desktop' iconStyle='regular' className='fa-xs mr-x1' />
        System
      </>
    )
  }
];

interface Props {
  trigger: ReactNode;
}

const DropdownNavItems = ({trigger}: Props) => {
  const {pathname} = window.location;
  const {dropdownNavItems} = useNavItems();

  const [showFeedbackModal, setShowFeedbackModal] = React.useState(false);
  const {firstName} = useGlobalNavUser();

  const {
    themePreference,
    setThemePreference,
    isFeatureEnabled: isThemeFeatureEnabled,
    isLoading: isThemeLoading
  } = React.useContext(ThemeContext);
  const isAuthenticated = useFluxStore(sessionStore, (s) => s.hasValidSession());

  const handleFeedbackModalToggle = () => {
    setShowFeedbackModal((prev) => !prev);
  };

  const shouldShowAppearanceControl = isAuthenticated && isThemeFeatureEnabled && !isThemeLoading;
  const selectedAppearanceIdx = APPEARANCE_OPTIONS.findIndex(
    ({value}) => value === themePreference
  );

  return (
    <>
      <Dropdown
        trigger={trigger}
        position='bottom-end'
        closeOnItemClicked
        wrapperClassName='global-nav-dropdown'
        trayClassName='z-10! mt-x1 text-nowrap'
        trayProps={{
          modifiers: {
            preventOverflow: {
              padding: 8
            }
          }
        }}
      >
        <div className='gap-x1 p-x1 flex flex-col' data-testid='global-nav__dropdown'>
          {dropdownNavItems.map(
            ({action, hardLink = false, icon, key, name, route, showSeparator = false}) => {
              const dataTestId = `global-nav__dropdown-link-${key}`;
              const iconComponent =
                icon === undefined ? null : <Icon icon={icon} className='mr-x1' />;

              if (key === 'feedback') {
                return (
                  <MenuListItem
                    key={key}
                    className='nav-submenu_item'
                    onClick={handleFeedbackModalToggle}
                    data-testid={dataTestId}
                  >
                    {iconComponent}
                    {name}
                  </MenuListItem>
                );
              }

              let dropdownItem;

              if (route !== undefined) {
                dropdownItem = hardLink ? (
                  <MenuListItem
                    as='a'
                    key={key}
                    href={route}
                    className='nav-submenu_item'
                    selected={pathname === route}
                    data-testid={dataTestId}
                  >
                    {iconComponent}
                    {name}
                  </MenuListItem>
                ) : (
                  <MenuListItem
                    as={Link}
                    to={route}
                    key={key}
                    className='nav-submenu_item'
                    selected={pathname === route}
                    data-testid={dataTestId}
                  >
                    {iconComponent}
                    {name}
                  </MenuListItem>
                );
              } else {
                dropdownItem = (
                  <MenuListItem
                    onClick={action}
                    key={key}
                    className='nav-submenu_item'
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        action?.();
                      }
                    }}
                    data-testid={dataTestId}
                  >
                    {iconComponent}
                    {name}
                  </MenuListItem>
                );
              }

              if (showSeparator) {
                return [
                  dropdownItem,
                  <div className='nav-submenu_border-line' key={`${key}-separator`} />
                ];
              }
              return dropdownItem;
            }
          )}
          {shouldShowAppearanceControl && (
            <>
              <div className='nav-submenu_border-line' />
              {/* Dropdown closes on any bubbled click (closeOnItemClicked) — trap clicks so picking a mode keeps the menu open */}
              {/* oxlint-disable-next-line click-events-have-key-events, no-static-element-interactions */}
              <div
                className='p-x1'
                data-testid='global-nav__appearance-control'
                onClick={(e) => e.stopPropagation()}
              >
                <Text as='div' size='xs' className='px-x1-half pb-x1'>
                  Appearance
                </Text>
                <SegmentedControl
                  buttonClassName='font-normal'
                  options={APPEARANCE_OPTIONS}
                  selectedIdx={selectedAppearanceIdx}
                  onSelect={(value) => setThemePreference(value as ThemePreference)}
                />
              </div>
            </>
          )}
        </div>
      </Dropdown>
      {showFeedbackModal && (
        <Modal
          className='customer-feedback-modal'
          ariaLabel='Customer Feedback Modal'
          handleClose={handleFeedbackModalToggle}
          longModal
        >
          {() => (
            <CustomerFeedbackDialogue
              name={firstName}
              handleClose={handleFeedbackModalToggle}
              inModal
            />
          )}
        </Modal>
      )}
    </>
  );
};

export default DropdownNavItems;
